<template lang="html">
  <section
    class="custom-display-element-member-row"
    :class="{ expanded: hasDataSource }"
    :id="id"
  >
    <div class="content">
      <div v-if="member.custom" class="custom-member-name">
        <input class="text-input" v-model.trim="member.name" />
      </div>
      <div v-else class="member-name">
        <span v-if="member.required" class="required-asterisk">*</span
        >{{ memberName }}
      </div>
      <div class="expression">
        <div v-if="member.isArray">
          <search-box
            :id="`data-source-${member.name}`"
            :box-type="'deEditModal'"
            :has-icon="false"
            :label-only="true"
            :list="dataElementList"
            :value="selectedDataSource"
            @on-search-box-item-selected="onSourceSelected"
          />
        </div>
        <expressions
          v-else
          :id="`expression-${member.name}`"
          :data-elements="allSources"
          :data-type="member.custom ? 'any' : dataType"
          :is-global="true"
          :initial-expression="initialExpression"
          :content-type="appType"
          @on-expression-field-valid="onConfigExpressionValid"
        />
      </div>
      <svg
        v-if="member.custom"
        tabindex="0"
        @keydown.enter.stop="toggleConfirmModal"
        @click.stop="toggleConfirmModal"
      >
        <use
          xlink:href="@/assets/icons/utility-sprite/svg/symbols.svg#delete"
        />
      </svg>
    </div>
    <div class="member-props" v-if="hasDataSource">
      <custom-display-element-expand-member
        :id="`member-props-${member.name}`"
        :member="member"
        :translations="translations"
        :all-sources="allSources"
        :data-source="dataSource"
        :properties="properties"
        :custom-member-fields="customMemberFields"
        :app-type="appType"
        @on-member-expression-update="onMemberExpressionSelected"
        @on-member-prop-delete="onMemberPropDelete"
      />
    </div>
    <simple-modal
      :show="displaySimpleModal"
      :message="getMessageText(messages.ARE_YOU_SURE)"
      :cancel-text="getMessageText(messages.CANCEL)"
      :ok-text="getMessageText(messages.OK)"
      @on-simple-modal-cancel="onCancelSimpleModal"
      @on-simple-modal-ok="onConfirmSimpleModal"
    />
  </section>
</template>

<script lang="js">
  import distributionChannelUtil from "@/utils/distribution-channel-util";
  import expressionBuilder from "@/views/drag-drop-pages/components/utils/expression-builder";
  import SimpleModal from "@/views/drag-drop-pages/components/modal/SimpleModal";
  import Expressions from "@/components/Expressions";
  import SearchBox from "@/components/SearchBox";
  import CustomDisplayElementExpandMember from "@/components/custom-display-elements/page-elements/CustomDisplayElementExpandMember";

  export default  {
    name: "custom-display-element-member-row",
    components: {
      SimpleModal,
      Expressions,
      SearchBox,
      CustomDisplayElementExpandMember
    },
    props: {
      id: String,
      translations: {
        type: Object,
        default: function() {
          return {};
        }
      },
      member: {
        type: Object,
        default: function() {
          return {};
        }
      },
      allSources: {
        type: Object,
        default: function() {
          return {};
        }
      },
      // properties from manifest
      properties: {
        type: Object,
        default: function() {
          return {};
        }
      },
      // custom properties that are not from the manifest file
      customMemberProperties: {
        type: Object,
        default: function() {
          return {};
        }
      },
      // custom member fields that are not from the manifest file
      customMemberFields: {
        type: Object,
        default: function() {
          return {};
        }
      },
      appType: {
        type: String
      }
    },
    data () {
      return {
        dataSource: null,
        displaySimpleModal: false
      }
    },
    computed: {
      messages() {
        return this.$store.state.messages;
      },
      dataType() {
        return distributionChannelUtil.getDataType(this.member);
      },
      dataElementList() {
        return this.dataSources.map(e => {
          e.label = e.name;
          return e;
        });
      },
      dataSources() {
        return expressionBuilder.filterGlobalSources(this.allSources);
      },
      selectedDataSource() {
        return this.dataSource? {
          id: this.dataSource.id,
          label: this.dataSource.name
        } : null;
      },
      hasDataSource() {
        return Boolean(this.selectedDataSource);
      },
      memberName() {
        return distributionChannelUtil.memberMessageTranslation(this.translations, this.member);
      },
      isValidMember() {
        return distributionChannelUtil.isValidMember(this.member);
      },
      propertyExpression() {
        return this.properties[this.member.name]? this.properties[this.member.name].expression : null;
      },
      customMemberPropertyExpression() {
        return this.customMemberProperties[this.member.customId]? this.customMemberProperties[this.member.customId].expression : null;
      },
      initialExpression() {
        return this.propertyExpression || this.customMemberPropertyExpression;
      }
    },
    mounted() {
      this.$nextTick(() => {
        if(Object.keys(this.properties).length) {
          const memberProperty = this.properties[this.member.name];
          if(memberProperty) {
            this.dataSource = this.allSources[memberProperty.dataSource];
          }
        }
      });
    },
    methods: {
      onCancelSimpleModal() {
        this.toggleConfirmModal();
      },
      onConfirmSimpleModal() {
        this.toggleConfirmModal();
        this.$emit("on-custom-member-remove", {member: this.member});
      },
      toggleConfirmModal() {
        this.displaySimpleModal = !this.displaySimpleModal;
      },
      onConfigExpressionValid(ctx) {
        const confExpression = ctx.status? ctx.confExpression : null;
        let newMember = JSON.parse(JSON.stringify(this.member));
        newMember.type.text = ctx.status? ctx.confExpression.dataType : "";
        this.$emit("on-expression-selected", {
          id: this.id,
          member: this.member.custom ? newMember: this.member,
          expression: confExpression
        });
        // this.member.type = newMember.type;
      },
      onSourceSelected(ctx) {
        if(ctx.isEmpty) {
          this.dataSource = null;
        } else {
          this.dataSource = ctx;
        }
        this.$emit("on-member-data-source-selected", {
          id: this.id,
          member: this.member,
          dataSource: this.dataSource
        });
      },
      onMemberExpressionSelected(ctx) {
        this.$emit("on-member-prop-expression-selected", {
          id: this.id,
          member: this.member,
          memberProp: ctx.memberProp,
          expression: ctx.expression
        });
      },
      onMemberPropDelete(ctx) {
        this.$emit("on-member-prop-data-delete", {
          id: this.id,
          member: this.member,
          memberProp: ctx.memberProp
        });
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "@/styles/components/main";
  .custom-display-element-member-row {
    border-bottom: 1px solid var(--LINE_GREY, #cdcdcd);
    .content {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      .member-name {
        display: flex;
        align-items: center;
        gap: 0.2rem;
        width: 25%;
        word-break: break-word;
      }
      .custom-member-name {
        width: 20%;
        .text-input {
          @include mixin-text-input;
        }
      }
      .expression {
        width: stretch;
      }
      svg {
        width: 1.5rem;
        height: 1.5rem;
        cursor: pointer;
        fill: var(--BUTTON_DESTRUCTIVE_BKGND, #a61a14);
      }
    }
    .member-props {
      box-sizing: border-box;
      padding-left: 2rem;
      padding-right: 1rem;
    }
    &.expanded {
      border-bottom: 3px solid var(--GREY, #eceff1);
    }
  }
</style>
